import { motion } from "framer-motion";
import {
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useDispatch } from "react-redux";
import * as filestack from "filestack-js";
import _ from "lodash";
import GradientPicker2 from "@/components/builder/config/GradentPicker2";
import AgentAvatarPicker from "@/pages/cbb-builder/components/Channels/components/shared/AgentAvatarPicker";
import BrandLogoUploader from "@/pages/cbb-builder/components/Channels/components/shared/BrandLogoUploader";
import { botThemes } from "@/pages/cbb-builder/AiBotData";
import { BotContext } from "@/pages/cbb-builder/store";
import environment from "@/constants/Environment";
import { userSlice } from "@/redux/User";
import type { AppearancePreviewTab } from "../types";
import useBotSave from "./../../cbb-builder/react-query/useBotSave";
import getTextColor from "@/utils/getTextColor";

const preMadeBotAvatars = [
  "https://dlvkyia8i4zmz.cloudfront.net/Sbo9LjGyTG2xV5b3QbpD_Group_1618874894.svg",
  "https://dlvkyia8i4zmz.cloudfront.net/XtPbs5FyRQaU9OC3MUpj_Group_1618874895.svg",
  "https://dlvkyia8i4zmz.cloudfront.net/zAGWWClxS3KPCQ0kxYda_Group_1618874896.svg",
];

const DEFAULT_THEME_COLOR = "#FB5F66";
const DEFAULT_WELCOME_COLOR =
  "linear-gradient(180deg, #FF0F7B 0%, #F89B29 100%)";
const filestackClient = filestack.init(environment().FILESTACK_API_KEY);

const getFileNameFromUrl = (url?: string) => {
  if (!url) return "";

  try {
    const pathname = new URL(url).pathname;
    const fileName = pathname.split("/").filter(Boolean).pop() ?? "";
    return decodeURIComponent(fileName) || "Brand logo";
  } catch {
    const fileName = url.split("?")[0].split("/").filter(Boolean).pop() ?? "";
    return decodeURIComponent(fileName) || "Brand logo";
  }
};

const UploadLogoIcon = () => {
  return (
    <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
      <path
        d="M9.63 22.91C6.88 22.72 4.72 20.43 4.72 17.63C4.72 14.69 7.1 12.31 10.04 12.31C10.57 8.76 13.62 6.04 17.31 6.04C21.36 6.04 24.65 9.33 24.65 13.38V13.68C27.36 14.15 29.42 16.51 29.42 19.36C29.42 22.55 26.83 25.13 23.65 25.13H20.75"
        stroke="#FB5F66"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 27.96V16.86M12.65 21.2L17 16.86L21.35 21.2"
        stroke="#FB5F66"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const LightIcon = ({ fill }: { fill?: string }) => {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.00033 12.3332C10.3936 12.3332 12.3337 10.3931 12.3337 7.99984C12.3337 5.6066 10.3936 3.6665 8.00033 3.6665C5.60709 3.6665 3.66699 5.6066 3.66699 7.99984C3.66699 10.3931 5.60709 12.3332 8.00033 12.3332Z"
        stroke={fill || "#666666"}
        strokeWidth={1.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.7597 12.7602L12.673 12.6735M12.673 3.32683L12.7597 3.24016L12.673 3.32683ZM3.23967 12.7602L3.32634 12.6735L3.23967 12.7602ZM7.99967 1.38683V1.3335V1.38683ZM7.99967 14.6668V14.6135V14.6668ZM1.38634 8.00016H1.33301H1.38634ZM14.6663 8.00016H14.613H14.6663ZM3.32634 3.32683L3.23967 3.24016L3.32634 3.32683Z"
        stroke={fill || "#666666"}
        strokeWidth={1.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const DarkIcon = ({ fill }: { fill?: string }) => {
  return (
    <svg
      width={15}
      height={15}
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0.617716 7.54999C0.857716 10.9833 3.77105 13.7767 7.25772 13.93C9.71772 14.0367 11.9177 12.89 13.2377 11.0833C13.7844 10.3433 13.491 9.84999 12.5777 10.0167C12.131 10.0967 11.671 10.13 11.191 10.11C7.93105 9.97666 5.26438 7.24999 5.25105 4.02999C5.24438 3.16333 5.42438 2.34333 5.75105 1.59666C6.11105 0.769994 5.67772 0.37666 4.84438 0.729994C2.20438 1.84333 0.397716 4.50333 0.617716 7.54999Z"
        stroke={fill || "#666666"}
        strokeWidth={1.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const setNestedValue = (
  data: Record<string, any>,
  path: string,
  value: any,
) => {
  const keys = path.split(".");
  const nextData = { ...data };
  let current = nextData;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value;
      return;
    }

    current[key] = { ...(current[key] ?? {}) };
    current = current[key];
  });

  return nextData;
};

const FieldLabel = ({ label, count }: { label: string; count?: string }) => {
  return (
    <label className="flex flex-col gap-[8px] text-[#444]">
      <span className="text-[14px] font-medium leading-[110%] text-[#444]">
        {label}
        {count && (
          <span className="ml-[6px] text-[12px] font-light font-normal text-[#666]">
            ({count})
          </span>
        )}
      </span>
    </label>
  );
};

type BotAppearanceSetupProps = {
  appearancePreviewTab?: AppearancePreviewTab;
};

const BotAppearanceSetup = ({
  appearancePreviewTab = "welcome",
}: BotAppearanceSetupProps) => {
  const [store, storeDispatch] = useContext(BotContext);
  const dispatch = useDispatch();
  const [logoFileName, setLogoFileName] = useState("");

  const chatConfig = store.botData?.chatConfig ?? {};
  const themeColor = chatConfig?.userBubbleColor ?? DEFAULT_THEME_COLOR;
  const themeMode = chatConfig?.theme ?? "light";
  const greetingTitle = chatConfig?.greetingConfig?.header?.text ?? "Hello";
  const subtitle =
    chatConfig?.greetingConfig?.description?.text ??
    "Enhance your business with AI - Voice Coach";
  const { mutateAsync: saveBot, status: updateStatus } = useBotSave();
  const isSubtitleEnabled =
    chatConfig?.greetingConfig?.description?.enabled ?? true;
  const ctaLabel = chatConfig?.welcomePageTxt ?? "Start New Conversation";
  const welcomeColor = chatConfig?.welcomePageBgColor ?? DEFAULT_WELCOME_COLOR;
  const brandLogoFileName =
    logoFileName || getFileNameFromUrl(chatConfig?.logo);

  const debouncedSaveBot = useCallback(
    _.debounce((data: any, save: (data: any) => void) => {
      save(data);
    }, 500),
    [],
  );

  const updateBotData = (path: string, value: any) => {
    const updatedData = setNestedValue(store.botData ?? {}, path, value);
    storeDispatch({
      type: "set-botData",
      botData: updatedData,
    });
    debouncedSaveBot(updatedData, saveBot);
  };

  const updateChatConfig = (path: string, value: any) => {
    if (path === "userBubbleColor") {
      updateBotData("chatConfig", {
        ...chatConfig,
        userBubbleColor: value,
        sendBtnBgColor: value,
      });
      return;
    }

    updateBotData(`chatConfig.${path}`, value);
  };

  const uploadCustomIcon = (type: "botAvatar" | "logo") => {
    const picker = filestackClient.picker({
      accept: ["image/jpeg", "image/png", "image/jpg"],
      fromSources: ["local_file_system", "url", "imagesearch"],
      transformations: {
        crop: false,
        circle: true,
        rotate: true,
      },
      maxFiles: 1,
      minFiles: 1,
      maxSize: 2097152,
      onFileUploadFinished: (data: any) => {
        const uploadedUrl = `https://dlvkyia8i4zmz.cloudfront.net/${encodeURIComponent(
          data?.key,
        )}`;

        if (type === "logo") {
          setLogoFileName(data?.filename ?? "Uploaded_logo.png");
          updateChatConfig("logo", uploadedUrl);
          return;
        }

        updateChatConfig("botAvatar", {
          ...(chatConfig?.botAvatar ?? {}),
          isPreMade: false,
          svgId: 0,
          url: uploadedUrl,
        });
      },
      onFileUploadFailed: () => {
        dispatch(
          userSlice.actions.setToastPopUp({
            show: true,
            toastType: "error",
            message:
              "Error uploading file. Please ensure it is a valid image (JPG, PNG) under 2MB.",
          }),
        );
      },
    });

    picker.open();
  };

  const handleLogoRemove = () => {
    setLogoFileName("");
    updateChatConfig("logo", "");
  };

  const handleThemeChange = (nextTheme: "light" | "dark") => {
    const themeValues = botThemes[nextTheme];
    const currentGreetingConfig = chatConfig?.greetingConfig ?? {};
    const currentGreetingTypography = currentGreetingConfig?.typography ?? {};
    const currentTypography = chatConfig?.typography ?? {};

    const newBotData = {
      ...(store.botData ?? {}),
      chatConfig: {
        ...chatConfig,
        chatBgColor: themeValues.chatBgColor,
        botBubbleColor: themeValues.botBubbleColor,
        messageBoxBgColor: themeValues.messageBoxBgColor,
        greetingConfig: {
          ...currentGreetingConfig,
          typography: {
            ...currentGreetingTypography,
            color: themeValues.greetingConfig.typography.color,
          },
        },
        typography: {
          ...currentTypography,
          color: getTextColor(themeValues.userBubbleColor),
        },
        theme: nextTheme,
      },
    };

    storeDispatch({
      type: "set-botData",
      botData: newBotData,
    });
    debouncedSaveBot(newBotData, saveBot);
  };

  useEffect(() => {
    setTimeout(() => {
      const iframe = document.querySelector(
        ".ai-bot-preview",
      ) as HTMLIFrameElement;
      if (iframe && iframe.contentWindow) {
        try {
          iframe.contentWindow.postMessage(
            {
              type: "BOT_DATA_UPDATE",
              chatConfig: chatConfig,
              botData: store.botData,
              timestamp: Date.now(),
            },
            "*",
          );
        } catch (err) {
          console.error("Error posting message to iframe:", err);
        }
      }
    }, 500);
  }, [updateChatConfig, chatConfig, store?.botData]);

  return (
    <motion.div
      className="flex flex-col gap-[25px]"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
    >
      {appearancePreviewTab === "conversation" ? (
        <div key="chat-preview" className="grid grid-cols-2 gap-[20px]">
          <div className="flex flex-col gap-[10px]">
            <h3 className="text-[14px] font-medium leading-[110%] text-[#444]">
              Agent Avatar
            </h3>
            <div className="flex min-h-[201px] flex-col items-center justify-center rounded-[10px] bg-[#f9f9f9] px-[16px] py-[30px]">
              <AgentAvatarPicker
                avatars={preMadeBotAvatars}
                botAvatar={chatConfig?.botAvatar}
                onChange={(avatar) => updateChatConfig("botAvatar", avatar)}
                onUploadClick={() => uploadCustomIcon("botAvatar")}
              />

              <span className="mt-[17px] rounded-[18px] bg-[#eeeeee] px-[16px] py-[8px] text-[14px] font-normal leading-[110%] text-[#555]">
                Max File Size: 2 MB
              </span>
              <p className="mt-[6px] text-center text-[13px] leading-[145%] text-[#777]">
                Supported formats: JPG, PNG, JPEG
                <br />
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-[10px]">
            <h3 className="text-[14px] font-medium leading-[110%] text-[#444]">
              Brand Logo
            </h3>
            <BrandLogoUploader
              logo={chatConfig?.logo}
              fallbackIcon={<UploadLogoIcon />}
              onUploadClick={() => uploadCustomIcon("logo")}
              onRemove={handleLogoRemove}
              uploadedFileName={brandLogoFileName}
              className="!min-h-[201px] !rounded-[10px] !bg-[#fafafa]"
            />
          </div>

          <ThemeColorInput
            label="User Bubble Color"
            value={themeColor}
            onChange={(value) => updateChatConfig("userBubbleColor", value)}
          />

          <div className="flex flex-col gap-[10px]">
            <h3 className="text-[14px] font-medium leading-[110%] text-[#444]">
              Theme Mode
            </h3>
            <div className="grid h-[46px] grid-cols-2 overflow-hidden rounded-[12px] bg-[#f9f9f9] p-[4px]">
              {(["light", "dark"] as const).map((mode) => {
                const isActive = themeMode === mode;

                return (
                  <motion.button
                    key={mode}
                    className={`relative flex items-center justify-center gap-[10px] rounded-[10px] text-[14px] capitalize leading-[110%] transition-[color,transform] duration-200 ${
                      isActive ? "text-[#FB5F66]" : "text-[#666]"
                    }`}
                    type="button"
                    whileTap={{ scale: 0.985 }}
                    onClick={() => handleThemeChange(mode)}
                  >
                    {isActive && (
                      <motion.div
                        className="absolute inset-0 rounded-[10px] bg-white shadow-[0_8px_20px_rgba(0,0,0,0.10)]"
                        layoutId="theme-mode-pill"
                        transition={{
                          type: "spring",
                          stiffness: 420,
                          damping: 34,
                          mass: 0.72,
                        }}
                      />
                    )}
                    <span className="relative z-[8] flex items-center gap-[10px]">
                      <motion.span>
                        {mode === "light" ? (
                          <LightIcon fill={isActive ? "#FB5F66" : undefined} />
                        ) : (
                          <DarkIcon fill={isActive ? "#FB5F66" : undefined} />
                        )}
                      </motion.span>
                      <motion.span
                        animate={{
                          opacity: isActive ? 1 : 0.84,
                          y: isActive ? 0 : 0.4,
                        }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                      >
                        {mode}
                      </motion.span>
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div key="welcome-screen" className="flex flex-col gap-[20px]">
          <div className="flex w-full items-end gap-[20px]">
            <div className="min-w-0 flex-1">
              <TextInput
                label="Welcome Title"
                count={`${greetingTitle.length}/50 Used`}
                value={greetingTitle}
                maxLength={50}
                onChange={(value) =>
                  updateChatConfig("greetingConfig.header.text", value)
                }
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-[8px]">
                <div className="flex min-h-[24px] items-center justify-between">
                  <FieldLabel
                    label="Subtitle"
                    count={`${subtitle.length}/42 Used`}
                  />
                  <div className="toggle-btn flex items-center">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={isSubtitleEnabled}
                        onChange={(event) =>
                          updateChatConfig(
                            "greetingConfig.description.enabled",
                            event.target.checked,
                          )
                        }
                      />
                      <span className="toggle round"></span>
                    </label>
                  </div>
                </div>
                <TextInputShell disabled={!isSubtitleEnabled}>
                  <input
                    className="w-full"
                    value={subtitle}
                    maxLength={42}
                    disabled={!isSubtitleEnabled}
                    onChange={(event) =>
                      updateChatConfig(
                        "greetingConfig.description.text",
                        event.target.value,
                      )
                    }
                  />
                </TextInputShell>
              </div>
            </div>
          </div>

          <div className="flex w-full items-end gap-[20px]">
            <div className="min-w-0 flex-1">
              <TextInput
                label="CTA Label"
                count={`${ctaLabel.length}/30 Used`}
                value={ctaLabel}
                maxLength={30}
                onChange={(value) => updateChatConfig("welcomePageTxt", value)}
              />
            </div>

            <div className="min-w-0 flex-1">
              <GradientTextInput
                label="Primary Theme Color"
                value={welcomeColor}
                onChange={(value) =>
                  updateChatConfig("welcomePageBgColor", value)
                }
              />
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

const TextInput = ({
  label,
  count,
  value,
  maxLength,
  onChange,
}: {
  label: string;
  count: string;
  value: string;
  maxLength: number;
  onChange: (value: string) => void;
}) => {
  return (
    <div className="flex flex-col gap-[8px]">
      <div className="flex min-h-[24px] items-center">
        <FieldLabel label={label} count={count} />
      </div>
      <TextInputShell>
        <input
          className="w-full"
          value={value}
          maxLength={maxLength}
          onChange={(event) => onChange(event.target.value)}
        />
      </TextInputShell>
    </div>
  );
};

const TextInputShell = ({
  children,
  disabled = false,
}: {
  children: ReactNode;
  disabled?: boolean;
}) => {
  return (
    <div
      className={`w-full min-w-[200px] rounded-[12px] bg-[#F9F9F9] p-[4px] ${
        disabled ? "opacity-50" : ""
      }`}
    >
      <div className="color__inputs !mt-[0]">{children}</div>
    </div>
  );
};

const HEX_COLOR_REGEX = /^#(?:[0-9A-F]{3}|[0-9A-F]{6}|[0-9A-F]{8})$/i;
const HEX_WITHOUT_HASH_REGEX = /^(?:[0-9A-F]{3}|[0-9A-F]{6}|[0-9A-F]{8})$/i;
const RGB_COLOR_REGEX =
  /^rgba?\(\s*(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\s*,\s*(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\s*,\s*(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i;

const normalizeHexColor = (color: string) => {
  const trimmedColor = color.trim();
  return HEX_WITHOUT_HASH_REGEX.test(trimmedColor)
    ? `#${trimmedColor}`
    : trimmedColor;
};

const isValidHexOrRgbColor = (color: string): boolean => {
  const normalizedColor = normalizeHexColor(color);
  return (
    HEX_COLOR_REGEX.test(normalizedColor) ||
    RGB_COLOR_REGEX.test(normalizedColor)
  );
};

const extractGradientColorTokens = (gradient: string) => {
  const matches = gradient.match(
    /#(?:[0-9A-F]{1,8})\b|(?:rgb|rgba)\([^)]+\)/gi,
  );
  return matches ?? [];
};

const hasInvalidGradientColorTokens = (gradient: string) => {
  const colorTokens = extractGradientColorTokens(gradient);

  return colorTokens.some((token) => !isValidHexOrRgbColor(token));
};

const isValidGradientBackground = (color: string): boolean => {
  if (!color.trim()) return false;
  if (hasInvalidGradientColorTokens(color)) return false;

  const element = document.createElement("div");
  element.style.background = color.trim();
  return element.style.background !== "";
};

const isValidThemeColorValue = (color: string): boolean =>
  isValidHexOrRgbColor(color) || isValidGradientBackground(color);

const ThemeColorInput = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState(value);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setInputValue(value);
    setHasError(false);
  }, [value]);

  const handleInputChange = (val: string) => {
    setInputValue(val);
    const checkVal = normalizeHexColor(val);

    if (isValidHexOrRgbColor(checkVal)) {
      setHasError(false);
      onChange(checkVal);
    } else {
      setHasError(true);
    }
  };

  const handleBlur = () => {
    const checkVal = normalizeHexColor(inputValue);

    if (!isValidHexOrRgbColor(checkVal)) {
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: "Please enter a valid hex or rgb color.",
        }),
      );
      setInputValue(value);
      setHasError(false);
      return;
    }

    setInputValue(checkVal);
    setHasError(false);
  };

  return (
    <div className="flex flex-col gap-[10px]">
      <h3 className="text-[14px] font-medium leading-[110%] text-[#444]">
        {label}
      </h3>
      <div
        className={`h-[46px] w-full min-w-[200px] rounded-[12px] border p-[3px] transition-colors duration-200 ${
          hasError
            ? "border-[#FB5F66] bg-[#FFF0F0]"
            : "border-transparent bg-[#F9F9F9]"
        }`}
      >
        <div className="color__inputs !mt-[0] h-[38px]">
          <div
            className="color__box"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <GradientPicker2
              initialColor={value}
              onGradientChange={(color) => onChange(color)}
              usePortal={true}
            />
          </div>
          <input
            type="text"
            value={inputValue}
            aria-invalid={hasError}
            onChange={(event) => handleInputChange(event.target.value)}
            onBlur={handleBlur}
          />
        </div>
      </div>
    </div>
  );
};

const GradientTextInput = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState(value);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setInputValue(value);
    setHasError(false);
  }, [value]);

  const handleInputChange = (val: string) => {
    setInputValue(val);
    const checkVal = normalizeHexColor(val);

    if (isValidThemeColorValue(checkVal)) {
      setHasError(false);
      onChange(checkVal);
    } else {
      setHasError(true);
    }
  };

  const handleBlur = () => {
    const checkVal = normalizeHexColor(inputValue);

    if (!isValidThemeColorValue(checkVal)) {
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: "Please enter a valid hex color or gradient.",
        }),
      );
      setInputValue(value);
      setHasError(false);
      return;
    }

    setInputValue(checkVal);
    setHasError(false);
  };

  return (
    <div className="flex flex-col gap-[10px]">
      <h3 className="text-[14px] font-medium leading-[110%] text-[#444]">
        {label}
      </h3>
      <div
        className={`w-full min-w-[200px] rounded-[12px] border p-[3px] transition-colors duration-200 ${
          hasError
            ? "border-[#FB5F66] bg-[#FFF0F0]"
            : "border-transparent bg-[#F9F9F9]"
        }`}
      >
        <div className="color__inputs !mt-[0]">
          <div
            className="color__box"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <GradientPicker2
              initialColor={value}
              onGradientChange={(color) => onChange(color)}
              usePortal={true}
            />
          </div>
          <input
            type="text"
            value={inputValue}
            aria-invalid={hasError}
            onChange={(event) => handleInputChange(event.target.value)}
            onBlur={handleBlur}
          />
        </div>
      </div>
    </div>
  );
};

export default BotAppearanceSetup;
